import { TwtSimpleJob } from '~/tenants/twt/performable/TwtSimpleJob';
import { DisplayData, TenantJobLine, TenantOrderLine } from '~/lib/model';
import Big from 'big.js';
import { TwtPropertyType } from '~/tenants/twt/model/TwtOrder';
import { FIRST_VERSION_TIMESTAMP, ZodVersionedMetadata } from '~/lib/zod';
import { DeliverableType } from '~common/model/Deliverable';
import { TwtOrderType } from '~/tenants/twt/model/enums';
import TwtNucleusConfig from '~/tenants/twt/performable/nucleus/TwtNucleusConfig';
import { twt_version, TwtVersion } from '~/tenants/twt/data';

export const TWT_NUCLEUS_EXTERIOR_SCAN_TIERS = [
  { max: 3000, price: 125, payout: 55 },
  { max: 8000, price: 150, payout: 85 },
  { max: 10000, price: 250, payout: 125 },
];

export default class TwtNucleusJob extends TwtSimpleJob<typeof TwtNucleusConfig> {
  protected get config() {
    return {
      onsiteMinimum: 90,
      onsitePer1000: 0,
      expenseMinimum: twt_version(this.order.created, TwtVersion.V21) ? this.isPackage ? 25 : 70 : 55,
      expensePer1000: 30
    };
  }

  protected get isPackage(){
    return this.orderHasPhotos && this.orderHasFloorplan;
  }

  get performable() {
    return TwtNucleusConfig;
  }

  get deliverable() {
    return DeliverableType.NUCLEUS;
  }

  get short() {
    return 'N3D';
  }

  defaultValue(): ZodVersionedMetadata<(typeof TwtNucleusConfig)['schema']> {
    return {
      version: FIRST_VERSION_TIMESTAMP,
    };
  }

  /**
   * Flat 90 minutes (the config above) unless the assigned provider
   * carries `nucleus_onsite_*` bands — a family of its own, so a
   * provider's Nucleus timings are independent of their Matterport
   * (`scan_onsite_*`) bands. With no bands set this is exactly the
   * pre-band behaviour: 90 at every square footage.
   *
   * Transfers are always 0, regardless of bands (a separate, since-
   * corrected change had gated banding behind a V27 appointment check
   * using Matterport's `scan_onsite_*` fields by mistake — Nucleus
   * bands are `nucleus_onsite_*`, never shared with Matterport's, and
   * apply unconditionally like every other band family).
   */
  onsite(): number {
    if (this.order.metadata.transfer) {
      return 0;
    }

    return this.onsiteWithProviderBands('nucleus_onsite');
  }

  info(): DisplayData[] {
    const info = super.info();

    if (this.metadata.branded_url) {
      info.push({
        name: 'Branded URL',
        value: this.metadata.branded_url,
        provider: true,
      });
    }

    if (this.metadata.unbranded_url) {
      info.push({
        name: 'Unbranded URL',
        value: this.metadata.unbranded_url,
        provider: true,
      });
    }

    if(this.metadata.videos){
      info.push({
        name: 'Videos',
        value: `${this.metadata.videos}`,
        provider: true,
        acknowledge: true,
        schedule: true,
        customer: true,
      });
    }

    if(this.metadata.hotspots && this.metadata.hotspots > 0){
      info.push({
        name: 'Hot Spots',
        value: `${this.metadata.hotspots}`,
        schedule: true,
        provider: true
      });
    }

    if(this.metadata.guided && this.metadata.guided > 0){
      info.push({
        name: 'Guided Tour Packs',
        value: `${this.metadata.guided}`,
        schedule: true,
        provider: true
      });
    }

    if (this.metadata.detached && this.metadata.detached.length > 0) {
      info.push({
        name: 'Detached Structures',
        value: this.metadata.detached.join(','),
        provider: true,
        acknowledge: true,
        schedule: true,
        customer: true,
      });
    }

    if (this.order.jobs.some(j => j.performable_id == 'nucleus-video')) {
      info.push({
        name: 'Nucleus Video',
        value: 'Yes',
        provider: true,
        acknowledge: true,
      });
    }

    return info;
  }

  get eligible(): boolean {
    return this.order.metadata.subtype !== TwtPropertyType.LAND && this.order.metadata.type !== TwtOrderType.LONG_TERM_RENTAL && this.order.metadata.type !== TwtOrderType.SHORT_TERM_RENTAL;
  }

  get configurable() {
    return true;
  }

  private get detached() {
    return this.metadata.detached ?? [];
  }

  private get hasExteriorCharge() {
    return this.detached.length > 0 && twt_version(this.order.created, TwtVersion.V22);
  }

  protected revenueMinimum() {
    return this.orderHasPhotos && this.orderHasFloorplan ? 96 : 125;
  }

  protected revenuePer() {
    return this.orderHasPhotos && this.orderHasFloorplan ? 0 : 80;
  }

  protected expensePer(thousands: number) {
    if(twt_version(this.order.created, TwtVersion.V21)){
      if(this.isPackage){
        return 10;
      }

      return thousands < 4 ? 5 : 10;
    }

    return thousands < 3 ? 35 : 30;
  }

  expenseLines(): TenantJobLine[] {
    let amount = this.simpleExpense();
    const lines: TenantJobLine[] = [];

    if (this.order.metadata.type === TwtOrderType.COMMERCIAL) {
      amount = new Big('0.07').times(this.sqft);
    }

    lines.push({
      amount,
      description: 'Nucleus Scan',
      id: 'nucleus',
    });

    if (this.hasExteriorCharge) {
      lines.push({
        amount: new Big(TwtNucleusJob.exterior(this.sqft).payout),
        description: 'Exterior Nucleus Scan',
        id: 'exterior',
      });
    }

    return lines;
  }

  static guided(value: number){
    if (value === 25) {
      return 65;
    } else if (value === 50) {
      return 120;
    } else if (value === 100) {
      return 195;
    }

    return 0;
  }

  static hotspots(value: number){
    if (value === 20) {
      return 39;
    } else if (value === 40) {
      return 72;
    } else if (value === 100) {
      return 155;
    } else if (value === 500) {
      return 585;
    }

    return 0;
  }

  static exterior(sqft: number) {
    return TWT_NUCLEUS_EXTERIOR_SCAN_TIERS.find((tier) => sqft <= tier.max) ?? TWT_NUCLEUS_EXTERIOR_SCAN_TIERS.at(-1)!;
  }

  revenueLines(): TenantOrderLine[] {
    const lines = super.revenueLines();

    let amount = this.simpleRevenue().amount;

    lines.push({
      amount,
      description: twt_version(this.order.created, TwtVersion.V22) ? 'Nucleus Scan (Interior)' : 'Nucleus Scan',
      id: 'nucleus',
      discountable: true,
      taxable: true,
    });

    if (this.metadata.guided && this.metadata.guided > 0) {
      let guided = new Big(0);

      if (this.metadata.guided === 25) {
        guided = new Big(65);
      } else if (this.metadata.guided === 50) {
        guided = new Big(120);
      } else if (this.metadata.guided === 100) {
        guided = new Big(195);
      }

      lines.push({
        amount: guided,
        description: `Guided Tour Packs (${this.metadata.guided})`,
        id: 'guided',
        discountable: true,
        taxable: true,
      });
    }

    if (this.metadata.hotspots && this.metadata.hotspots > 0) {
      let hotspots = new Big(TwtNucleusJob.hotspots(this.metadata.hotspots));

      if(hotspots.gt(0)) {
        lines.push({
          amount: hotspots,
          description: `Hot Spots (${this.metadata.hotspots})`,
          id: 'hotspots',
          discountable: true,
          taxable: true,
        });
      }
    }

    if (this.metadata.videos && this.metadata.videos > 0) {
      lines.push({
        amount: new Big(175).times(this.metadata.videos),
        description: `Videos (${this.metadata.videos})`,
        id: 'videos',
        discountable: true,
        taxable: true,
      });
    }

    if (this.hasExteriorCharge) {
      lines.push({
        amount: new Big(TwtNucleusJob.exterior(this.sqft).price),
        description: 'Exterior Nucleus Scan',
        id: 'exterior',
        discountable: true,
        taxable: true,
      });
    }

    return lines;
  }
}
